import { Box, Grid2 } from '@mui/material';
import CustomButton from '@ui-components/Button/CustomButton';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import { useNavigate, useParams } from 'react-router-dom';
import FormField from '@dynamicForm/FormFields';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import moment from 'moment';
import CustomAutoCompleteSingleSelection from '@ui-components/AutoComplete/CustomAutoCompleteSingleSelection';
import CustomDivider from '@ui-components/Divider/CustomDivider';
import CustomSelect from '@ui-components/Select/CustomSelect';
import {
  CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH,
  CONSULT_WAITING_SCREEN_ROUTE_PATH,
  FACILITY_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH,
} from '../../routers/RoutPath';
import {
  useConsultsMetaData,
  useGetConsultDetailsById,
  useGetIntakeFormForConsult,
  useSaveConsult,
  useSaveOrUpdateConsult,
} from '../../API/hooks/consultHook';
import type { ConsultForm, ConsultResponse } from '../../API/hooks/consultHook';
import { getUserFromStorage, hasRole } from '../components/storageUtility';
import CustomDialog from '@qler-view/shared_lib/src/shared/lib/ui-components/Dialog/CustomDialog';
import CustomDialogContent from '@ui-components/Dialog/CustomDialogContent';
import CustomDialogActions from '@ui-components/Dialog/CustomDialogActions';
import { ROLES } from '../../roles';
import { initAudio, warmUpAudio } from '../../shared/lib/utils/audioManager';
import AlertSound from '@assets/ringtone.mp3';
import {
  useOnHoldConsultMetadata,
  useSaveOnHoldConsult,
} from '../../shared/statusRequest/StatusRequestSetup';
import { PORTAL_TYPES } from '@qler-view/shared_lib/src';
import OnHoldConsultModel, {
  type OnHoldReasonFacilityParams,
} from './../../../../../shared_lib/src/shared/statusrequests/OnHoldConsultModel';
import CustomSnackbar from '@qler-view/shared_lib/src/shared/lib/ui-components/Snackbar/CustomSnackbar';

export interface ConsultFormValues {
  facilityId: number;
  sessionStatusId: number;
  patientFirstName: string;
  patientLastName: string;
  dateOfBirth: string;
  patientMrn: string;
  callBackNumber: string;
  reasonForConsult: string;
  unitOrDepartment: string;
  referringProvider: string;
  cart: string;
  sessionId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

const CreateConsult = () => {
  const navigate = useNavigate();
  const user = getUserFromStorage();
  const userId = user?.userId ?? 0;
  const { consultId } = useParams<{ consultId?: string }>();
  const initialSessionStatusRef = useRef<number | null>(null);

  const isCaseManager = user?.role?.toString() === ROLES.CASE_MANAGER;
  const isFacilityUser = !isCaseManager;

  const { ConsultsMetaData } = useConsultsMetaData(userId);

  const [facilityId, setFacilityId] = useState<number>(
    isFacilityUser ? (user?.facilityId ?? 0) : 0
  );

  const [committedFacilityId, setCommittedFacilityId] = useState<number | null>(
    isFacilityUser ? (user?.facilityId ?? null) : null
  );

  const [pendingFacilityId, setPendingFacilityId] = useState<number | null>(
    null
  );
  const [showFacilityConfirm, setShowFacilityConfirm] = useState(false);

  const [sessionStatus, setSessionStatus] = useState<number>(0);
  const [previousStatus, setPreviousStatus] = useState<number | null>(null);
  const [previousAssignedTo, setPreviousAssignedTo] = useState<number | null>(
    null
  );
  const [openOnHoldConsult, setOpenOnHoldConsult] = useState(false);

  const [openToast, setOpenToast] = useState(false);
  const [message, setMessage] = useState('');
  const [toastType, setToastType] = useState<'error' | 'success' | 'info'>(
    'error'
  );

  const [onHoldConsultValues, setOnHoldConsultValues] =
    useState<OnHoldReasonFacilityParams>({} as OnHoldReasonFacilityParams);

  const { OnHoldConsultMetadata } = useOnHoldConsultMetadata(
    PORTAL_TYPES.FACILITY
  );

  const { DynamicIntakeForm, isLoading: DynamicIntakeFormLoading } =
    useGetIntakeFormForConsult(
      userId.toString(),
      facilityId,
      Number(consultId ?? 0)
    );
  const { mutate: onHoldConsult } = useSaveOnHoldConsult();

  const { ConsultDetailsById } = useGetConsultDetailsById(
    userId.toString(),
    Number(consultId ?? 0)
  );

  const [consultSubmitAction, setConsultSubmitAction] = useState<string>('');

  const { mutate: saveConsult, isPending: isSaving } = useSaveConsult(
    (response) => {
      if (isCaseManager) {
        navigate(`${CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH}`, {
          state: { create: 'The consult request has been canceled.' },
        });
      } else {
        navigate(
          `${CONSULT_WAITING_SCREEN_ROUTE_PATH}?consultId=${response.id}`,
          { replace: true }
        );
      }
      if (sessionStatus === statusByName.ON_HOLD) {
        handleSubmitOnHoldReason(response.id);
      }
    }
  );

  const onUpdateSuccess = (_response: ConsultResponse) => {
    if (sessionStatus === statusByName.ON_HOLD) {
      handleSubmitOnHoldReason(Number(consultId));
    }
    if (hasRole([ROLES.CASE_MANAGER])) {
      navigate(CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH, {
        state: { update: 'Consult updated successfully.' },
      });
    } else {
      navigate(FACILITY_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH, {
        state: { update: 'Consult updated successfully.' },
      });
    }
  };

  const { mutate: saveOrUpdateConsult, isPending: isUpdating } =
    useSaveOrUpdateConsult(onUpdateSuccess);
  const statusByName = useMemo(() => {
    const map: Record<string, number> = {};
    (ConsultsMetaData?.createStatusList ?? []).forEach((s) => {
      map[s.name.toUpperCase().replaceAll(/\s+/g, '_')] = s.id;
    });
    return map;
  }, [ConsultsMetaData?.createStatusList]);

  useEffect(() => {
    if (statusByName.NEW && !sessionStatus) {
      setSessionStatus(statusByName.NEW);
    }
  }, [statusByName, sessionStatus]);

  const { control, handleSubmit, setValue, reset } = useForm<ConsultFormValues>(
    {
      mode: 'onChange',
      defaultValues: {
        facilityId: isFacilityUser ? (user?.facilityId ?? 0) : 0,
        sessionStatusId: 0,
        patientFirstName: '',
        patientLastName: '',
        dateOfBirth: '',
        patientMrn: '',
        callBackNumber: '',
        reasonForConsult: '',
        unitOrDepartment: '',
        referringProvider: '',
        cart: '',
        sessionId: '',
      },
    }
  );

  const mapConsultToFormValues = (
    consult: ConsultForm
  ): Partial<ConsultFormValues> => {
    const base = {
      facilityId: consult.facilityId,
      sessionStatusId: consult.sessionStatusId,
      sessionId: consult.sessionId,
      patientFirstName: consult.sessionFields.patientFirstName,
      patientLastName: consult.sessionFields.patientLastName,
      dateOfBirth: consult.sessionFields.dateOfBirth,
      patientMrn: consult.sessionFields.patientMrn,
      callBackNumber: consult.sessionFields.callBackNumber,
      reasonForConsult: consult.sessionFields.reasonForConsult,
      unitOrDepartment: consult.sessionFields.unitOrDepartment,
      referringProvider: consult.sessionFields.referringProvider,
      cart: consult.sessionFields.cart ?? '',
    };

    const dynamicFields = consult.additionalSessionFields?.reduce<
      Record<string, string | number>
    >((acc, f) => {
      acc[f.fieldName] = String(f.fieldValue ?? '');
      return acc;
    }, {});

    return { ...base, ...dynamicFields };
  };

  useEffect(() => {
    if (!ConsultDetailsById) return;
    setFacilityId(ConsultDetailsById.facilityId);
    setValue('facilityId', ConsultDetailsById.facilityId);
    setValue('sessionStatusId', ConsultDetailsById.sessionStatusId);
    setSessionStatus(ConsultDetailsById.sessionStatusId);
    setPreviousStatus(ConsultDetailsById.sessionStatusId);
    setPreviousAssignedTo(ConsultDetailsById.consultAssignedToProviderId);
  }, [ConsultDetailsById]);

  useEffect(() => {
    if (!ConsultDetailsById || !DynamicIntakeForm) return;
    const formValues = mapConsultToFormValues(ConsultDetailsById);
    reset(formValues);
  }, [DynamicIntakeForm, ConsultDetailsById, reset]);

  useEffect(() => {
    if (
      consultId &&
      initialSessionStatusRef.current === null &&
      sessionStatus !== undefined
    ) {
      initialSessionStatusRef.current = sessionStatus;
    }
  }, [consultId, sessionStatus]);

  const handelBack = () => {
    if (hasRole([ROLES.CASE_MANAGER])) {
      navigate(CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH);
    } else {
      navigate(FACILITY_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH);
    }
  };

  const applyFacilityChange = (newFacilityId: number | null) => {
    setCommittedFacilityId(newFacilityId);
    setFacilityId(newFacilityId ?? 0);
    setValue('facilityId', newFacilityId ?? 0, {
      shouldValidate: true,
    });
    setShowFacilityConfirm(false);
    setPendingFacilityId(null);
  };

  const cancelFacilityChange = () => {
    setShowFacilityConfirm(false);
    setPendingFacilityId(null);
  };

  const getSubmitButtonText = (): string => {
    if (isSaving || isUpdating) {
      return 'Submitting...';
    }
    if (consultId) {
      return 'Update';
    }
    return 'Submit';
  };

  const onSubmit: SubmitHandler<ConsultFormValues> = (values): void => {
    // Unlock audio on this real user gesture (needed for iOS/iPadOS)
    initAudio(AlertSound);
    warmUpAudio();
    const payload: ConsultForm = {
      id: consultId ? Number(consultId) : 0,
      facilityId: isFacilityUser ? (user?.facilityId ?? 0) : values.facilityId,
      sessionStatusId:
        values.sessionStatusId && values.sessionStatusId > 0
          ? values.sessionStatusId
          : statusByName.NEW,
      sessionId: consultSubmitAction === 'Update' ? values.sessionId : '',
      consultAssignedToProviderId: 0,
      sessionFields: {
        patientFirstName: values.patientFirstName,
        patientLastName: values.patientLastName,
        dateOfBirth: values.dateOfBirth
          ? moment.utc(values.dateOfBirth).format('YYYY-MM-DD') +
            'T00:00:00.000Z'
          : '',
        patientMrn: values.patientMrn,
        callBackNumber: values.callBackNumber,
        reasonForConsult: values.reasonForConsult,
        unitOrDepartment: values.unitOrDepartment,
        referringProvider: values.referringProvider,
        cart: values.cart,
      },
      additionalSessionFields:
        DynamicIntakeForm?.additionalFields.map((field) => {
          const fieldValue = values[field.fieldName as keyof ConsultFormValues];
          let formattedValue = '';

          if (fieldValue) {
            if (field.type === 'Date') {
              formattedValue =
                moment.utc(fieldValue).format('YYYY-MM-DD') + 'T00:00:00.000Z';
            } else {
              formattedValue = String(fieldValue);
            }
          }

          return {
            id: Number(field.id),
            fieldName: field.fieldName,
            fieldValue: formattedValue,
          };
        }) ?? [],
    };

    if (consultId) {
      saveOrUpdateConsult({
        userId: userId.toString(),
        action: consultSubmitAction,
        ...payload,
      });
    } else {
      saveConsult({
        userId: userId.toString(),
        ...payload,
      });
    }
  };

  const handleSubmitOnHoldReason = (consultId: number) => {
    onHoldConsult(
      {
        userId: userId.toString(),
        portalType: PORTAL_TYPES.FACILITY,
        consultId: consultId,
        ...onHoldConsultValues,
      },
      {
        onSuccess: () => {
          setOpenOnHoldConsult(false);
        },
      }
    );
  };

  const getPreviousName = () => {
    const statusEntry = Object.entries(statusByName).find(
      ([, value]) => value === previousStatus
    );
    const statusName = statusEntry?.[0];

    const formattedStatus = statusName
      ? statusName
          .replaceAll('_', ' ')
          .toLowerCase()
          .replaceAll(/\b\w/g, (c) => c.toUpperCase())
      : 'New';

    setMessage(
      `The consult has been reverted to its previous status "${formattedStatus}".`
    );
    setToastType('error');
    setOpenToast(true);
    setSessionStatus(previousStatus ?? statusByName.NEW);
    setValue('sessionStatusId', previousStatus ?? 0, {
      shouldValidate: true,
    });
  };

  return (
    <Box>
      {openOnHoldConsult && (
        <OnHoldConsultModel
          openOnHoldConsultPopup={openOnHoldConsult}
          onCloseOnHoldConsultPopup={() => {
            setOpenOnHoldConsult(false);
            if (Object.keys(onHoldConsultValues).length === 0) {
              getPreviousName();
              if (consultId) {
                setValue('assignedTo', previousAssignedTo ?? 0, {
                  shouldValidate: true,
                });
              }
            }
          }}
          ConsultOnHoldMetaData={OnHoldConsultMetadata ?? undefined}
          onSubmitOnHoldConsult={(values: OnHoldReasonFacilityParams) => {
            setOnHoldConsultValues(values);
            setOpenOnHoldConsult(false);
          }}
          initialValues={onHoldConsultValues}
        />
      )}
      <Box>
        <Grid2 container className="text-align-left">
          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
            <CustomTypography id="create-consult" variant="h4">
              {consultId
                ? 'Edit Consult Request'
                : 'Create New Consult Request'}
            </CustomTypography>
            {!consultId && (
              <CustomTypography
                id="create-consult-subtext"
                variant="body3"
                color="textDisabled"
              >
                Fill form to request consult
              </CustomTypography>
            )}
          </Grid2>
        </Grid2>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box className="pt-36">
          {isCaseManager && (
            <>
              <Box className="display-flex">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-name-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Facility Name&nbsp;<span className="color-red">*</span>
                    </CustomTypography>
                    <CustomAutoCompleteSingleSelection
                      id="facility-name"
                      placeholder="Select Facility Name"
                      size="small"
                      value={
                        ConsultsMetaData?.facilityList?.find(
                          (f) => f.id === facilityId
                        ) ?? null
                      }
                      options={ConsultsMetaData?.facilityList ?? []}
                      onChange={(selectedFacility) => {
                        const selectedFacilityId = selectedFacility
                          ? Number(selectedFacility.id)
                          : 0;

                        // First time selection – no confirm needed
                        if (!committedFacilityId) {
                          applyFacilityChange(selectedFacilityId);
                          return;
                        }

                        // User picked the same facility → do nothing
                        if (selectedFacilityId === committedFacilityId) return;

                        // User re-selected a different facility → ask for confirmation
                        setPendingFacilityId(selectedFacilityId);
                        setShowFacilityConfirm(true);
                        reset();
                      }}
                      disabled={Boolean(consultId)}
                      noOptionsText="No Facility Found"
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    {DynamicIntakeForm && (
                      <Box>
                        <CustomTypography
                          id="session-state-label"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          Consult Status <span className="color-red">*</span>
                        </CustomTypography>

                        <CustomSelect
                          id="session-state"
                          placeholder="Select Status"
                          size="small"
                          selectedValue={sessionStatus ?? statusByName.NEW}
                          options={ConsultsMetaData?.createStatusList ?? []}
                          onChange={(val) => {
                            const statusId = Number(val);
                            setSessionStatus(statusId);
                            setValue('sessionStatusId', statusId, {
                              shouldValidate: true,
                            });
                          }}
                          onOptionClick={(val) => {
                            const statusId = Number(val);

                            if (statusId === statusByName.ON_HOLD) {
                              setOpenOnHoldConsult(true);
                            }
                          }}
                          fullWidth
                        />
                      </Box>
                    )}
                  </Grid2>
                </Grid2>
              </Box>

              <Box className="pt-32 pb-32">
                <CustomDivider />
              </Box>
            </>
          )}
          <Box>
            {DynamicIntakeFormLoading && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CustomTypography
                  id="loading-text"
                  variant="h5"
                  className="text-align-center pt-32"
                  color="textDisabled"
                >
                  Loading...
                </CustomTypography>
              </Box>
            )}
            {DynamicIntakeForm && (
              <>
                <Box>
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <CustomTypography
                        variant="h5"
                        className="text-align-left pb-32"
                        id="consult-details-text"
                      >
                        Patient Information
                      </CustomTypography>
                    </Grid2>
                  </Grid2>
                  <Grid2 container spacing={4} className="width-80">
                    {DynamicIntakeForm.sessionFields.map((field) => (
                      <Grid2 key={field.fieldName} size={{ xs: 12, md: 4 }}>
                        <CustomTypography
                          id={`field-label-${field.fieldName}`}
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          {field.fieldLabel}&nbsp;
                          {field.mandatory && (
                            <span className="color-red">*</span>
                          )}
                        </CustomTypography>
                        <FormField
                          field={field}
                          control={control}
                          dateOfBirth={true}
                          minimumAgeRequired={
                            DynamicIntakeForm.minimumAgeRequired
                          }
                        />
                      </Grid2>
                    ))}
                  </Grid2>
                </Box>

                {DynamicIntakeForm.additionalFields.length > 0 && (
                  <>
                    <Box className="pt-32">
                      <CustomDivider />
                    </Box>
                    <Box className="pt-32">
                      <Grid2 container spacing={4} className="width-80">
                        <Grid2
                          size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                        >
                          <CustomTypography
                            variant="h5"
                            className="text-align-left pb-32"
                            id="additional-consult-fields-text"
                          >
                            Additional Information
                          </CustomTypography>
                        </Grid2>
                      </Grid2>
                      <Grid2 container spacing={4} className="width-80">
                        {DynamicIntakeForm.additionalFields.map((field) => (
                          <Grid2 key={field.fieldName} size={{ xs: 12, md: 4 }}>
                            <CustomTypography
                              id={`field-label-${field.fieldName}`}
                              variant="body3"
                              color="textPrimary"
                              className="text-align-left pb-12"
                            >
                              {field.fieldLabel}&nbsp;
                              {field.mandatory && (
                                <span className="color-red">*</span>
                              )}
                            </CustomTypography>
                            <FormField field={field} control={control} />
                          </Grid2>
                        ))}
                      </Grid2>
                    </Box>
                  </>
                )}

                <Box className="pt-32 pb-32">
                  <Grid2 container>
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <Box className="display-flex justify-end">
                        <Box className="pr-12">
                          <CustomButton
                            id="back-consult"
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                              handelBack();
                            }}
                          >
                            Cancel
                          </CustomButton>
                        </Box>
                        <Box>
                          <CustomButton
                            id="create-consult"
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSaving || isUpdating}
                            onClick={() => {
                              setConsultSubmitAction(
                                consultId ? 'Update' : 'Save'
                              );
                            }}
                          >
                            {getSubmitButtonText()}
                          </CustomButton>
                        </Box>
                      </Box>
                    </Grid2>
                  </Grid2>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </form>

      <Box>
        <CustomDialog
          id="facility-change-dialog"
          open={showFacilityConfirm}
          onClose={cancelFacilityChange}
          disableEscapeKeyDown
        >
          <CustomDialogContent>
            <Box className="pb-8">
              <CustomTypography id="facility-change-title" variant="h4">
                Change Facility
              </CustomTypography>
            </Box>
            <Box>
              <CustomTypography
                id="facility-change-text"
                variant="body3"
                color="textDisabled"
              >
                Changing the facility will reset consult-related information
                that may have already been entered. Do you wish to proceed?
              </CustomTypography>
            </Box>
          </CustomDialogContent>

          <CustomDialogActions>
            <CustomButton
              id="cancel-button"
              variant="outlined"
              color="primary"
              onClick={cancelFacilityChange}
            >
              Cancel
            </CustomButton>

            <CustomButton
              id="proceed-button"
              variant="contained"
              color="primary"
              onClick={() => applyFacilityChange(pendingFacilityId)}
            >
              Proceed
            </CustomButton>
          </CustomDialogActions>
        </CustomDialog>
      </Box>

      {openToast && (
        <CustomSnackbar
          id="toast-consult-create"
          successOrError={toastType}
          open={openToast}
          handleClose={() => setOpenToast(false)}
          toastrMessage={message}
        />
      )}
    </Box>
  );
};

export default CreateConsult;
